import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { Typography, IconButton, Paper } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import CustCard from 'src/components/common/ActionCard';
import { COMMERCIAL_RELAUNCH, CONTENT_EDITOR, TEMPLATE_SELECTOR } from './MassActionMessage';
import ActionType from 'src/enums/actionType';
import { useAlert } from 'src/hooks';
import { CONTENT } from '../MassActionEditor';
import {
  useCheckContentFieldsAndSelectedColumns,
  useResetContentEditorFlowData,
} from 'src/components/massAction/state/massAction';
import { useMassActionState } from '../../MassActionStateContext';

const useStyles = makeStyles()(theme => ({
  addBtn: {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.primary.contrastText,
  },
}));

const ActionCreator = ({ actionTypeId, goToStep, lang, onCreate }) => {
  const { classes } = useStyles();
  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const checkContentFieldsAndSelectedColumns = useCheckContentFieldsAndSelectedColumns();
  const resetContentEditorFlowData = useResetContentEditorFlowData();
  const [, setMassActionState] = useMassActionState();

  const onCreateXplorActiveNotif = useCallback(() => {
    checkContentFieldsAndSelectedColumns(
      '',
      actionTypeId,
      contentFields => {
        onCreate();
        goToStep(CONTENT, {
          actionType: actionTypeId,
          actionId: null,
          initialStep: CONTENT_EDITOR,
          initialExtraProps: {
            fields: contentFields,
            content: '',
            object: '',
          },
        });
      },
      showAlert
    );
  }, [actionTypeId, checkContentFieldsAndSelectedColumns, goToStep, onCreate, showAlert]);

  const onCreateAddCommercialRelaunch = useCallback(() => {
    onCreate();
    setMassActionState(prevState => ({
      ...prevState,
      commercialRelaunch: {
        id: null,
        comment: null,
        date: 0,
        prospectingState: null,
        prospectingType: null,
        salePersonId: null,
        type: actionTypeId,
      },
    }));

    goToStep(CONTENT, {
      actionType: actionTypeId,
      initialStep: COMMERCIAL_RELAUNCH,
    });
  }, [actionTypeId, goToStep, onCreate, setMassActionState]);

  return (
    <CustCard title={t(lang.title[actionTypeId])}>
      <Paper style={{ height: '200px', width: '160px', display: 'inline-block' }}>
        <Typography
          color="primary"
          style={{
            wordWrap: 'normal',
            padding: '8px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {t(lang.createText[actionTypeId])}
        </Typography>

        <IconButton
          onClick={e => {
            e.preventDefault();
            resetContentEditorFlowData();
            if (ActionType.PUSH_XPLORACTIVE.id === actionTypeId) {
              onCreateXplorActiveNotif();
            } else if (ActionType.PROSPECTING.id === actionTypeId) {
              onCreateAddCommercialRelaunch();
            } else {
              goToStep(CONTENT, {
                actionType: actionTypeId,
                initialStep: TEMPLATE_SELECTOR,
              });
            }
          }}
          className={classes.addBtn}
          size="small"
        >
          <AddIcon />
        </IconButton>
      </Paper>
    </CustCard>
  );
};

ActionCreator.propTypes = {
  actionTypeId: PropTypes.number.isRequired,
  goToStep: PropTypes.func.isRequired,
  lang: PropTypes.object.isRequired,
  onCreate: PropTypes.func.isRequired,
};

export default ActionCreator;
